import { Loading } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback, useMemo, useState as useStateReact } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import * as Styled from '../styled';
import type { BookingSlotPickerFormValues, BookingSlotPickerProps } from '../types';

export const WorkerAvailabilitiesInput: FC<BookingSlotPickerProps> = (props) => {
  const {
    browseBookingDates,
    daysWithAvails,
    availabilitiesLoading,
    nextAvailability,
    daysDisplayedCount,
    fromDate,
    disabled = false,
  } = props;

  const { translate } = useTranslation();
  const { values, setFieldValue } = useFormikContext<BookingSlotPickerFormValues>();

  const [showAllAvails, setShowAllAvails] = useStateReact(false);
  const goToNextAvail = useCallback(() => {
    browseBookingDates('nextAvailability');
  }, [browseBookingDates]);
  const shouldDisplayAvails = !availabilitiesLoading && values.workerId && values.serviceId;

  const noAvailsToDisplay = daysWithAvails.every((day) => day.avails?.length === 0);
  const workerNoAvailibities = nextAvailability?.workerId === 0;

  const showOverlay = useMemo(() => {
    if (availabilitiesLoading) return true;
    if (!shouldDisplayAvails) return true;
    if (workerNoAvailibities) return true;
    if (noAvailsToDisplay) return true;
    return false;
  }, [availabilitiesLoading, shouldDisplayAvails, workerNoAvailibities, noAvailsToDisplay]);

  return (
    <>
      <Styled.CalendarContainer>
        {showOverlay && (
          <>
            {availabilitiesLoading && (
              <Styled.CalendarOverlayWrapper>
                <Styled.CalendarOverlay>
                  <Loading color="dark" type="puff" />
                </Styled.CalendarOverlay>
              </Styled.CalendarOverlayWrapper>
            )}

            {!shouldDisplayAvails && !availabilitiesLoading && (
              <Styled.CalendarOverlayWrapper>
                <Styled.CalendarOverlay>
                  {translate('common.inputs.availabilities.placeholder')}
                </Styled.CalendarOverlay>
              </Styled.CalendarOverlayWrapper>
            )}

            {shouldDisplayAvails &&
              noAvailsToDisplay &&
              !workerNoAvailibities &&
              nextAvailability && (
                <Styled.CalendarOverlayWrapper>
                  <Styled.CalendarOverlay onClick={goToNextAvail} clickable>
                    {translate('common.buttons.nextAvailability')}
                    <div color="transparent">
                      {dayjs(nextAvailability?.date).format('DD MMMM YYYY')}
                    </div>
                  </Styled.CalendarOverlay>
                </Styled.CalendarOverlayWrapper>
              )}

            {shouldDisplayAvails && workerNoAvailibities && (
              <Styled.CalendarOverlayWrapper>
                <Styled.CalendarOverlay onClick={() => browseBookingDates('past')} clickable>
                  {translate('common.inputs.availabilities.empty')}
                </Styled.CalendarOverlay>
              </Styled.CalendarOverlayWrapper>
            )}
          </>
        )}

        <Styled.CalendarArrowWrapper
          onClick={() => browseBookingDates('past')}
          css={shouldDisplayAvails && workerNoAvailibities && { borderBottom: '0px', zIndex: 7 }}
        >
          <FaChevronLeft />
        </Styled.CalendarArrowWrapper>

        {daysWithAvails.length ? (
          daysWithAvails.map((day) => {
            return (
              <Styled.CalendarColumn key={+day.date}>
                <Styled.CalendarDayContainer>
                  <div>{day.date.format('ddd')}</div>
                  <Styled.CalendarMonth>{day.date.format('DD MMM')}</Styled.CalendarMonth>
                </Styled.CalendarDayContainer>
                <Styled.CalendarHoursContainer showAllAvails={showAllAvails}>
                  {day.avails?.map((avail) => (
                    <Styled.CalendarHour
                      key={avail?.startDateTime}
                      selected={values.bookingStart === avail?.startDateTime}
                      onClick={() => {
                        if (disabled) return;
                        setFieldValue('bookingStart', avail?.startDateTime);
                      }}
                    >
                      {dayjs(avail?.startDateTime).format('HH:mm')}
                    </Styled.CalendarHour>
                  ))}
                </Styled.CalendarHoursContainer>
              </Styled.CalendarColumn>
            );
          })
        ) : (
          <>
            {new Array(daysDisplayedCount).fill(undefined).map((_, index) => (
              <Styled.CalendarColumn key={index}>
                <Styled.CalendarDayContainer>
                  <div>{fromDate.add(index, 'day').format('ddd')}</div>
                  <Styled.CalendarMonth>
                    {fromDate.add(index, 'day').format('DD MMM')}
                  </Styled.CalendarMonth>
                </Styled.CalendarDayContainer>
                <Styled.CalendarHoursContainer>
                  <Styled.CalendarHour>11:30</Styled.CalendarHour>
                  <Styled.CalendarHour>12:30</Styled.CalendarHour>
                  <Styled.CalendarHour>13:30</Styled.CalendarHour>
                  <Styled.CalendarHour>14:30</Styled.CalendarHour>
                </Styled.CalendarHoursContainer>
              </Styled.CalendarColumn>
            ))}
          </>
        )}
        <Styled.CalendarArrowWrapper onClick={() => browseBookingDates('future')}>
          <FaChevronRight />
        </Styled.CalendarArrowWrapper>
      </Styled.CalendarContainer>

      {!showOverlay && (
        <Styled.ShowMoreButton onClick={() => setShowAllAvails(!showAllAvails)}>
          {showAllAvails
            ? translate('common.buttons.showLessTimes')
            : translate('common.buttons.showMoreTimes')}
        </Styled.ShowMoreButton>
      )}
    </>
  );
};
